import React, { useCallback, useEffect, useState } from 'react';
import * as S from './styles';
import { useOrder, useStorage } from '../../hooks';
import {
  Topbar,
  Navbar,
  DashboardFooter,
  Breadcrumb,
  OrderCard,
  Pagination,
  DynamicFilter,
} from '../../components';
import LoadingAnimated from '../../assets/loading-animated.svg';
import {
  IDynamicFilterData,
  IDynamicFilterSelectOptions,
  IOrderProps,
} from '../../interfaces';
import { usePagination } from '../../hooks/usePagination';

export const Orders: React.FC = () => {
  const {
    errors,
    isLoading,
    loadedItems,
    getNewPaginatedOrders,
    qtdeRegistros,
  } = useOrder();

  const today = new Date();

  // 45 dias retroativos
  const startDate = new Date();
  startDate.setDate(today.getDate() - 45);

  // 12 meses retroativos
  const blockDate = new Date();
  blockDate.setMonth(today.getMonth() - 12);

  const { currentPage, pageSize, handlePageChange, resetPagination } =
    usePagination({
      totalItems: qtdeRegistros || 0,
      onPageChange: (currentPage, pageSize) => {
        getNewPaginatedOrders({
          ...filters,
          currentPage,
          pageSize,
        });
      },
    });

  const [filters, setFilters] = useState<IDynamicFilterData>({
    type: 'clienteNome',
    term: '',
    dateStart: startDate.toISOString().split('T')[0],
    dateEnd: today.toISOString().split('T')[0],
  });

  const searchOrders = async (filterData: IDynamicFilterData) => {
    resetPagination();
    await getNewPaginatedOrders({
      ...filterData,
      isNewSearch: true,
      currentPage: 1,
      pageSize: 20,
    });
  };

  const dataTreatment = useCallback((value: IDynamicFilterData) => {
    return {
      ...value,
      term: value.term.trim(),
    };
  }, []);

  const filterSelectOptions: IDynamicFilterSelectOptions[] = [
    {
      value: 'clienteNome',
      description: 'Nome do Cliente',
      placeholder: 'Digite o nome do cliente',
    },
    {
      value: 'cnpj',
      description: 'CNPJ',
      placeholder: 'Digite o CNPJ',
    },
    {
      value: 'numNota',
      description: 'Número da Nota Fiscal',
      placeholder: 'Digite apenas números da NF',
    },
    {
      value: 'numPedAllied',
      description: 'Número do Pedido Allied',
      placeholder: 'Digite o número do pedido Allied',
    },
    {
      value: 'numPedEcom',
      description: 'Número do Pedido E-Commerce',
      placeholder: 'Digite o número do pedido E-commerce',
    },
  ];

  useEffect(() => {
    getNewPaginatedOrders({
      ...filters,
      currentPage: 1,
      pageSize,
    });
  }, []);

  return (
    <S.Container>
      <Topbar />
      <Navbar />
      <S.Content>
        <Breadcrumb paths={paths} />
        <S.Title>Consulta de Pedidos</S.Title>

        <DynamicFilter
          filterSelectOptions={filterSelectOptions}
          filters={filters}
          setFilters={setFilters}
          isLoading={isLoading}
          searchFilters={searchOrders}
          dataTreatment={dataTreatment}
          calendar
          isDateRequired
        />
        <S.Orders>
          <S.TitleSmall>Pedidos</S.TitleSmall>
          <S.Items>
            {!isLoading &&
              loadedItems?.map(
                (element: IOrderProps, index: React.Key | null | undefined) => (
                  <OrderCard key={index} order={element} />
                ),
              )}
          </S.Items>
          <S.Items>
            {isLoading && <S.LoadingAnimated src={LoadingAnimated} />}
          </S.Items>
          <S.Items>{!isLoading && errors && <p>{errors}</p>}</S.Items>
        </S.Orders>

        {!isLoading && !errors?.length && (
          <Pagination
            currentPage={currentPage}
            paginationSize={pageSize}
            onPageChange={handlePageChange}
            totalItems={qtdeRegistros}
          />
        )}
      </S.Content>
      <DashboardFooter />
    </S.Container>
  );
};

const paths = [
  {
    title: 'Meu Painel',
    link: '/painel',
  },
  {
    title: 'Consulta de Pedidos',
    link: '/pedidos',
  },
];
